/* Native Shopify commerce: server prices, inventory enforcement and checkout. */
(() => {
  'use strict';
  function quantityError(value, rule = {}, allowZero = false) {
    const quantity = Number(value), min = Math.max(1, Number(rule.min) || 1), step = Math.max(1, Number(rule.step) || 1);
    const max = rule.max === null || rule.max === undefined || rule.max === '' ? Infinity : Number(rule.max);
    if (!Number.isSafeInteger(quantity) || quantity < 0) return 'Enter a whole-number quantity.';
    if (allowZero && quantity === 0) return '';
    if (quantity < min) return `The minimum quantity is ${min}.`;
    if (Number.isFinite(max) && quantity > max) return `The maximum available quantity is ${Math.max(0, max)}.`;
    if ((quantity - min) % step !== 0) return `Choose quantities in increments of ${step}, starting at ${min}.`;
    return '';
  }
  function selectedVariant(variants, id) { return variants.find(variant => String(variant.id) === String(id)) || null; }
  function errorMessage(payload, fallback) {
    if (typeof payload?.description === 'string') return payload.description;
    if (typeof payload?.message === 'string') return payload.message;
    if (typeof payload?.errors === 'string') return payload.errors;
    if (payload?.errors && typeof payload.errors === 'object') return Object.values(payload.errors).flat().filter(value => typeof value === 'string').join(' ') || fallback;
    return fallback;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {quantityError, selectedVariant, errorMessage};
  if (typeof document === 'undefined') return;
  const translate = value => window.EasternI18n?.t(value) || value;
  const notify = (root, message, error = false) => {
    const box = root.querySelector('[data-native-feedback]');
    if (!box) return;
    box.hidden = false; box.textContent = translate(message); box.classList.toggle('ej-native-error', error);
    box.setAttribute('role', error ? 'alert' : 'status'); box.focus({preventScroll: true});
  };
  async function request(url, data) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    let response, payload;
    try {
      response = await fetch(url, {method: 'POST', credentials: 'same-origin', signal: controller.signal, headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}, body: JSON.stringify(data)});
      payload = await response.json();
    } catch (_) {
      const error = new Error('The store could not confirm this update. Refresh your bag before trying again.');
      error.uncertain = true; throw error;
    } finally { clearTimeout(timeout); }
    if (!response.ok) { const error = new Error(errorMessage(payload, 'This selection could not be updated. Please review your bag and try again.')); if (response.status >= 500) error.uncertain = true; throw error; }
    return payload;
  }
  const setBusy = (form, busy) => {
    form.setAttribute('aria-busy', String(busy));
    form.querySelectorAll('button,input,select,textarea').forEach(control => {
      if (busy) { control.dataset.nativeWasDisabled = control.disabled ? 'true' : 'false'; control.disabled = true; }
      else { control.disabled = control.dataset.nativeWasDisabled === 'true'; delete control.dataset.nativeWasDisabled; }
    });
  };
  async function refreshCount(cartUrl) {
    try {
      const response = await fetch(`${cartUrl}.js`, {credentials: 'same-origin', headers: {'Accept': 'application/json'}});
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count],[data-native-cart-count]').forEach(node => {node.textContent = String(cart.item_count); node.hidden = cart.item_count === 0;});
      document.dispatchEvent(new CustomEvent('brand:cartchange', {detail: {cart}}));
    } catch (_) { /* The completed add is not retried if only the counter refresh failed. */ }
  }
  function initProduct(root) {
    if (root.dataset.nativeInitialized) return;
    root.dataset.nativeInitialized = 'true';
    let variants = [];
    try { variants = JSON.parse(root.querySelector('[data-native-variants]').textContent); } catch (_) { return; }
    const form = root.querySelector('[data-native-product-form]'), select = root.querySelector('[data-native-variant]'), quantity = root.querySelector('[data-native-quantity]'), add = root.querySelector('[data-native-add]');
    if (!form || !select || !quantity || !add) return;
    const purchaseButtons = [...root.querySelectorAll('[data-native-add],[data-native-mobile-add],[data-native-buy-now],[data-native-mobile-buy-now]')];
    let variant = selectedVariant(variants, select.value), busy = false, uncertain = false, busyIntent = 'add', checkoutNavigation = false;
    const isBuyNow = button => button?.matches('[data-native-buy-now],[data-native-mobile-buy-now]');
    const canPurchase = () => root.dataset.ready === 'true' && Boolean(variant?.available) && (variant.max === null || variant.max === undefined || variant.max >= variant.min);
    function renderPurchaseButtons() {
      const available = canPurchase();
      purchaseButtons.forEach(button => {
        button.disabled = !available || busy || uncertain;
        const intent = isBuyNow(button) ? 'buy' : 'add';
        const label = intent === 'buy' ? 'Buy now' : 'Add to bag';
        const progress = intent === 'buy' ? 'Opening checkout…' : 'Adding…';
        button.textContent = translate(!available ? 'Sold out' : busy && intent === busyIntent ? progress : label);
        button.setAttribute('aria-busy', String(busy));
      });
    }
    function validate() { const message = quantityError(quantity.value, variant || {}); quantity.setCustomValidity(message ? translate(message) : ''); return !message; }
    function renderVariant(updateUrl = false) {
      variant = selectedVariant(variants, select.value);
      if (!variant) { renderPurchaseButtons(); quantity.disabled = true; notify(root, 'This option is not available. Please choose another option.', true); return; }
      const available = canPurchase();
      root.querySelectorAll('[data-native-price]').forEach(price => {
        // Preserve the accepted price typography and its accessible label in both
        // the information column and the compact mobile purchase bar.
        const amount = price.querySelector('.ej-piece-price-amount') || price;
        amount.textContent = variant.price;
      });
      const sku = root.querySelector('[data-native-sku]'); if (sku) {sku.hidden = !variant.sku; sku.querySelector('[data-native-sku-value]').textContent = variant.sku || '';}
      root.querySelectorAll('[data-native-detail-sku]').forEach(node => {node.textContent = variant.sku || '';});
      root.querySelectorAll('[data-native-option]').forEach(node => {node.textContent = variant.title;});
      quantity.min = String(variant.min); quantity.step = String(variant.step);
      if (variant.max === null || variant.max === undefined) quantity.removeAttribute('max'); else quantity.max = String(variant.max);
      if (quantityError(quantity.value, variant)) quantity.value = String(variant.min);
      quantity.disabled = !available;
      renderPurchaseButtons();
      root.querySelectorAll('[data-native-availability]').forEach(node => { node.textContent = translate(available ? 'Available to order' : 'Sold out'); });
      const rule = root.querySelector('[data-native-quantity-rule]');
      if (rule) rule.textContent = [variant.min > 1 ? `Minimum ${variant.min}.` : '', variant.step > 1 ? `Increments of ${variant.step}.` : ''].filter(Boolean).map(translate).join(' ');
      validate();
      if (updateUrl) {
        const url = new URL(location.href); url.searchParams.set('variant', String(variant.id)); history.replaceState({}, '', url);
        const media = [...root.querySelectorAll('[data-native-media]')].find(node => node.dataset.nativeMedia === String(variant.mediaId));
        if (media) {
          const gallery = media.closest('[data-product-gallery]');
          const frames = gallery ? [...gallery.querySelectorAll('[data-gallery-frame]')] : [];
          const index = frames.findIndex(frame => frame === media || frame.contains(media));
          const thumbnail = index >= 0 ? gallery.querySelector(`[data-gallery-select="${index}"]`) : null;
          if (thumbnail) thumbnail.click();
          else media.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'nearest'});
        }
        const feedback = root.querySelector('[data-native-feedback]'); if (feedback) feedback.hidden = true;
      }
    }
    select.addEventListener('change', () => renderVariant(true));
    quantity.addEventListener('input', validate);
    function openCheckout() {
      // Shopify's cart POST starts checkout from the existing cart. No cart
      // clear, item replacement, customer data or payment submission occurs.
      const checkout = document.createElement('form');
      checkout.method = 'post'; checkout.action = root.dataset.cartUrl;
      checkout.hidden = true; checkout.dataset.nativeCheckoutRedirect = '';
      const field = document.createElement('input');
      field.type = 'hidden'; field.name = 'checkout'; field.value = 'Checkout';
      checkout.append(field); document.body.append(checkout);
      if (typeof checkout.requestSubmit === 'function') checkout.requestSubmit();
      else HTMLFormElement.prototype.submit.call(checkout);
    }
    form.addEventListener('submit', async event => {
      if (busy) {event.preventDefault(); return;}
      if (uncertain || !canPurchase() || !validate()) {event.preventDefault(); form.reportValidity(); return;}
      event.preventDefault(); busy = true;
      busyIntent = isBuyNow(event.submitter) ? 'buy' : 'add';
      const id = variant.id, count = Number(quantity.value);
      let itemAdded = false, checkoutStarted = false;
      setBusy(form, true); renderPurchaseButtons();
      try {
        await request(root.dataset.addUrl, {items: [{id, quantity: count}]});
        itemAdded = true;
        if (busyIntent === 'buy') {
          openCheckout(); checkoutStarted = true; checkoutNavigation = true;
        } else {
          notify(root, 'Added to your bag. You can review your selection and continue to checkout.');
          await refreshCount(root.dataset.cartUrl);
        }
      } catch (error) {
        uncertain = itemAdded || Boolean(error.uncertain);
        notify(root, itemAdded ? 'Your item was added, but checkout could not open. Review your bag to continue.' : error.message || 'Unable to confirm the update. Check your bag before trying again.', true);
      } finally {
        // Once checkout navigation is requested, keep every purchase entry
        // locked. A second click must never add the same selection twice.
        if (!checkoutStarted) {busy = false; setBusy(form, false); renderVariant(false);}
      }
    });
    root.addEventListener('brand:native-resume', () => {
      // A back-forward-cache return from checkout restores the old DOM,
      // including its disabled buttons. Resume only a known checkout handoff;
      // an ambiguous add result must remain locked until the page is refreshed.
      if (!checkoutNavigation) return;
      checkoutNavigation = false; busy = false;
      setBusy(form, false); renderVariant(false);
      refreshCount(root.dataset.cartUrl);
    });
    renderVariant(false);
  }
  function initCart(root) {
    if (root.dataset.nativeInitialized) return;
    root.dataset.nativeInitialized = 'true';
    const form = root.querySelector('[data-native-cart-form]'); if (!form) return;
    let busy = false;
    const quantities = [...form.querySelectorAll('[data-line-key]')];
    const validate = input => {
      const error = quantityError(input.value, {min: input.dataset.quantityMin, step: input.dataset.quantityStep, max: input.getAttribute('max')}, true);
      input.setCustomValidity(error ? translate(error) : ''); return !error;
    };
    quantities.forEach(input => input.addEventListener('input', () => validate(input)));
    const update = async (url, payload) => {
      if (busy) return;
      busy = true; setBusy(form, true);
      try {await request(url, payload); location.reload();}
      catch (error) {
        setBusy(form, false); busy = false; notify(root, error.message, true);
        // A server may adjust stock before returning an error. Reload before trusting cached totals.
        const checkout = form.querySelector('[data-native-checkout]'); if (checkout) checkout.disabled = true;
        const box = form.querySelector('[data-native-feedback]');
        const reload = document.createElement('a'); reload.href = location.href; reload.textContent = translate('Refresh bag'); reload.className = 'brand-text-link'; box.append(' ', reload);
      }
    };
    form.addEventListener('submit', event => {
      if (busy) {event.preventDefault(); return;}
      let valid = true; quantities.forEach(input => {if (!validate(input)) valid = false;});
      if (!valid) {event.preventDefault(); form.reportValidity(); return;}
      // Native checkout POST submits the latest quantities and note to Shopify, even without JavaScript.
      if (event.submitter?.name === 'checkout') return;
      event.preventDefault();
      const updates = Object.fromEntries(quantities.map(input => [input.dataset.lineKey, Number(input.value)]));
      update(root.dataset.updateUrl, {updates, note: form.querySelector('[name="note"]')?.value || ''});
    });
    root.querySelectorAll('[data-native-remove]').forEach(link => link.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); update(root.dataset.changeUrl, {id: link.dataset.nativeRemove, quantity: 0});
    }));
  }
  function initMedia(root) {
    if (root.dataset.mediaInitialized) return;
    root.dataset.mediaInitialized = 'true';
    const links = [...root.querySelectorAll('[data-native-zoom]')]; if (!links.length || !('HTMLDialogElement' in window)) return;
    let current = 0, opener = null;
    const dialog = document.createElement('dialog'); dialog.className = 'ej-native-lightbox'; dialog.setAttribute('aria-label', translate('Product image viewer'));
    const image = document.createElement('img');
    const close = document.createElement('button'); close.type = 'button'; close.className = 'ej-native-lightbox-close'; close.textContent = '×'; close.setAttribute('aria-label', translate('Close image viewer'));
    const prev = document.createElement('button'); prev.type = 'button'; prev.className = 'ej-native-lightbox-prev'; prev.textContent = '←'; prev.setAttribute('aria-label', translate('Previous image'));
    const next = document.createElement('button'); next.type = 'button'; next.className = 'ej-native-lightbox-next'; next.textContent = '→'; next.setAttribute('aria-label', translate('Next image'));
    const caption = document.createElement('p'); caption.className = 'ej-native-lightbox-count'; caption.setAttribute('aria-live', 'polite');
    dialog.append(close, image, prev, next, caption); root.append(dialog);
    const show = index => {current = (index + links.length) % links.length; image.src = links[current].href; image.alt = links[current].querySelector('img')?.alt || ''; caption.textContent = `${current + 1} / ${links.length}`;};
    links.forEach((link, index) => link.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault(); opener = link; show(index); dialog.showModal(); document.documentElement.classList.add('ej-native-modal-open'); close.focus();
    }));
    close.addEventListener('click', () => dialog.close()); prev.addEventListener('click', () => show(current - 1)); next.addEventListener('click', () => show(current + 1));
    dialog.addEventListener('keydown', event => {if (event.key === 'ArrowLeft') {event.preventDefault(); show(current - 1);} if (event.key === 'ArrowRight') {event.preventDefault(); show(current + 1);}});
    dialog.addEventListener('close', () => {document.documentElement.classList.remove('ej-native-modal-open'); opener?.focus({preventScroll: true});});
    if (links.length < 2) {prev.hidden = true; next.hidden = true;}
  }
  function init(scope = document) {
    scope.querySelectorAll('[data-native-product]').forEach(root => {initProduct(root); initMedia(root);});
    scope.querySelectorAll('[data-native-cart]').forEach(initCart);
  }
  init(); document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => {
    if (event.persisted) document.querySelectorAll('[data-native-product]').forEach(root => root.dispatchEvent(new Event('brand:native-resume')));
  });
})();
